import { JSX, useCallback, useMemo, useState } from 'react';

import type {
  IColumnConfig,
  UserProfileWithStructure,
  ColumnAlignType
} from './types';

interface IUserTableRowProps {
  readonly user: UserProfileWithStructure;
  readonly type?: 'upline' | 'downline' | 'selectedUser';
  readonly columns: IColumnConfig[];
  readonly className?: string | ((user: UserProfileWithStructure) => string);
  readonly containerWidth: number;
  readonly renderExpandedBlock?: (
    user: UserProfileWithStructure
  ) => JSX.Element | null;
}

const ALIGN_CLASSES: Record<ColumnAlignType, string> = {
  center: 'text-center',
  right: 'text-right',
  left: 'text-left'
};

export const UserTableRow = ({
  user,
  columns,
  className,
  containerWidth,
  renderExpandedBlock
}: IUserTableRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const context = useMemo(
    () => ({
      isExpanded,
      setIsExpanded
    }),
    [isExpanded]
  );

  const renderCell = useCallback(
    (column: IColumnConfig) => {
      const alignClass = ALIGN_CLASSES[column.align || 'left'];
      const baseClass = column.className ?? '';
      const dynamicClass =
        typeof column.cellClassName === 'function'
          ? column.cellClassName(user, context)
          : column.cellClassName ?? '';

      return (
        <td
          key={column.key}
          className={`${alignClass} ${baseClass} ${dynamicClass}`}
        >
          {column.render(user, context)}
        </td>
      );
    },
    [user, context]
  );

  return (
    <>
      <tr
        className={
          typeof className === 'function' ? className(user) : className
        }
      >
        {columns.map(renderCell)}
      </tr>

      {isExpanded && renderExpandedBlock && (
        <tr>
          <td
            className="bg-white overflow-visible border-y border-slate-100 p-0"
            colSpan={columns.length}
          >
            <div
              className="w-full h-full sticky left-0"
              style={{ width: containerWidth }}
            >
              {renderExpandedBlock(user)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
